import { firestoreService } from './firestore';
import {
  Cull,
  CreateCullRequest,
  CullQuery,
  DashboardStats,
} from '../types';

export const cullsApi = {
  getCulls: async (query?: CullQuery): Promise<Cull[]> => {
    return await firestoreService.getCulls(query);
  },

  getCull: async (id: string): Promise<Cull> => {
    return await firestoreService.getCull(id);
  },

  createCull: async (request: CreateCullRequest, userId: string): Promise<Cull> => {
    return await firestoreService.createCull(request, userId);
  },

  updateCull: async (id: string, request: Partial<CreateCullRequest>): Promise<Cull> => {
    return await firestoreService.updateCull(id, request);
  },

  deleteCull: async (id: string): Promise<void> => {
    return await firestoreService.deleteCull(id);
  },

  getUserCulls: async (userId: string, query?: CullQuery): Promise<Cull[]> => {
    return await firestoreService.getUserCulls(userId, query);
  },

  exportCsv: async (culls: Cull[]): Promise<Blob> => {
    // Client-side CSV export since we're no longer using backend
    const headers = [
      'Date', 'Time', 'Location', 'Species', 'Sex', 'Age', 'Weight', 'Condition',
      'Shot Placement', 'Shot Distance', 'Antlers Present', 'Antler Points', 'Antler Condition', 'Notes'
    ];
    
    const csvContent = [
      headers.join(','),
      ...culls.map(cull => [
        cull.date,
        cull.time,
        cull.location_name,
        cull.species,
        cull.sex,
        cull.age_estimate,
        cull.weight || '',
        cull.condition,
        cull.shot_placement,
        cull.shot_distance || '',
        cull.antlers_present ? 'Yes' : 'No',
        cull.antler_points || '',
        cull.antler_condition || '',
        cull.notes || ''
      ].map(field => `"${field}"`).join(','))
    ].join('\n');
    
    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  },
};

export const dashboardApi = {
  getStats: async (culls: Cull[]): Promise<DashboardStats> => {
    // Calculate stats client-side from Firestore data
    const totalCulls = culls.length;
    const thisMonth = culls.filter(cull => {
      const cullDate = new Date(cull.date);
      const now = new Date();
      return cullDate.getMonth() === now.getMonth() && cullDate.getFullYear() === now.getFullYear();
    }).length;
    
    const speciesCounts = culls.reduce((acc, cull) => {
      acc[cull.species] = (acc[cull.species] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const avgWeight = culls
      .filter(cull => cull.weight)
      .reduce((sum, cull, _, arr) => sum + (cull.weight! / arr.length), 0);
    
    return {
      total_culls: totalCulls,
      this_month: thisMonth,
      species_breakdown: speciesCounts,
      average_weight: Math.round(avgWeight * 10) / 10
    };
  },
};